/**
 * Calf ERP Settings Service
 * CRUD operations for CalfERPSettings collection
 * Stores farm-wide calf management configuration, KPI thresholds, and breed benchmarks
 *
 * @collection Users/{userId}/CalfERPSettings (single document: "config")
 */

import { db } from "../../../firebase";
import {
	PRODUCTION_TYPES,
	getProductionTypeConfig,
} from "../../../constants/productionTypes";

const COLLECTION = "CalfERPSettings";
const DOC_ID = "config";

// ============================================================================
// DEFAULT SETTINGS
// ============================================================================

export const DEFAULT_CALF_ERP_SETTINGS = {
	// Target weights (kg)
	targetBirthWeight: 40,
	targetWeaningWeight: 80,
	targetWeaningAge: 56, // days
	target90DayWeight: 120,

	// Growth
	targetADG: 0.8, // kg/day (birth to weaning)
	targetPostWeaningADG: 1.0, // kg/day (post-weaning)

	// KPI thresholds
	kpiThresholds: {
		mortalityRate: { good: 3, warning: 5, critical: 8 }, // percent
		adg: { good: 0.8, warning: 0.6, critical: 0.4 }, // kg/day
		vaccinationCompliance: { good: 95, warning: 85, critical: 70 }, // percent
		scoursIncidence: { good: 10, warning: 20, critical: 30 }, // percent
		brdIncidence: { good: 5, warning: 10, critical: 20 }, // percent
		avgWisconsinScore: { good: 2, warning: 4, critical: 6 }, // score
	},

	// Breed benchmarks (kg at weaning)
	breedBenchmarks: {
		holstein: { birthWeight: 42, weaningWeight: 85, adg: 0.80 },
		jersey: { birthWeight: 27, weaningWeight: 60, adg: 0.60 },
		angus: { birthWeight: 35, weaningWeight: 100, adg: 0.90 },
		simmental: { birthWeight: 42, weaningWeight: 95, adg: 0.95 },
		charolais: { birthWeight: 45, weaningWeight: 105, adg: 1.00 },
		limousin: { birthWeight: 38, weaningWeight: 95, adg: 0.90 },
		hereford: { birthWeight: 37, weaningWeight: 90, adg: 0.85 },
		"belgian blue": { birthWeight: 46, weaningWeight: 110, adg: 1.00 },
		crossbred: { birthWeight: 40, weaningWeight: 90, adg: 0.85 },
	},

	// Alert settings
	alerts: {
		vaccinationOverdueDays: 7, // days past due before alert
		weightCheckIntervalDays: 14, // expected weight check frequency
		faecalScoreThreshold: 2, // score >= this triggers alert
	},
};

/**
 * Defaults for fattening cattle (feedlot) operations.
 * Stored under the "fattening" section of the same config document, so
 * existing calf settings (document root) stay untouched.
 */
export const DEFAULT_FATTENING_ERP_SETTINGS = {
	// Target weights (kg)
	targetEntryWeight: 200, // typical stocker/feeder entry weight
	targetFinishWeight: 650, // live weight at slaughter
	targetDaysOnFeed: 330,

	// Growth
	targetADG: 1.3, // kg/day across the fattening period

	// KPI thresholds
	kpiThresholds: {
		mortalityRate: { good: 1, warning: 2, critical: 4 }, // percent
		adg: { good: 1.3, warning: 1.0, critical: 0.8 }, // kg/day
		vaccinationCompliance: { good: 95, warning: 85, critical: 70 }, // percent
		brdIncidence: { good: 5, warning: 10, critical: 20 }, // percent
		lamenessIncidence: { good: 5, warning: 10, critical: 15 }, // percent
		// kg feed (as fed) per kg live-weight gain, lower is better;
		// intensive bull fattening typically lands between 5.5 and 8
		fcr: { good: 6.5, warning: 8.5, critical: 10.5 },
	},

	// Alert settings
	alerts: {
		vaccinationOverdueDays: 7,
		weightCheckIntervalDays: 30, // fattening cattle are weighed less often
	},
};

const DEFAULT_SETTINGS_BY_TYPE = {
	[PRODUCTION_TYPES.CALF]: DEFAULT_CALF_ERP_SETTINGS,
	[PRODUCTION_TYPES.FATTENING]: DEFAULT_FATTENING_ERP_SETTINGS,
};

// ============================================================================
// WRITE OPERATIONS
// ============================================================================

/**
 * Save calf ERP settings (creates or overwrites)
 * @param {string} userId
 * @param {Object} settings
 */
export async function saveCalfERPSettings(userId, settings) {
	const cleanSettings = Object.fromEntries(
		Object.entries(settings).filter(([, v]) => v !== undefined)
	);

	await db
		.collection("Users")
		.doc(userId)
		.collection(COLLECTION)
		.doc(DOC_ID)
		.set(
			{ ...cleanSettings, updatedAt: Date.now() },
			{ merge: true }
		);
}

/**
 * Update specific fields in calf ERP settings
 */
export async function updateCalfERPSettings(userId, updates) {
	await db
		.collection("Users")
		.doc(userId)
		.collection(COLLECTION)
		.doc(DOC_ID)
		.update({ ...updates, updatedAt: Date.now() });
}

// ============================================================================
// READ OPERATIONS
// ============================================================================

/**
 * Get calf ERP settings (with defaults)
 * @param {string} userId
 * @returns {Promise<Object>} Merged settings (user overrides + defaults)
 */
export async function getCalfERPSettings(userId) {
	const doc = await db
		.collection("Users")
		.doc(userId)
		.collection(COLLECTION)
		.doc(DOC_ID)
		.get();

	if (!doc.exists) {
		return { ...DEFAULT_CALF_ERP_SETTINGS };
	}

	// Deep merge user settings over defaults
	const userSettings = doc.data();
	return deepMerge(DEFAULT_CALF_ERP_SETTINGS, userSettings);
}

// ============================================================================
// HELPERS
// ============================================================================

/**
 * Deep merge two objects (target values override source)
 */
function deepMerge(source, target) {
	const result = { ...source };
	for (const key of Object.keys(target)) {
		if (
			target[key] &&
			typeof target[key] === "object" &&
			!Array.isArray(target[key]) &&
			source[key] &&
			typeof source[key] === "object"
		) {
			result[key] = deepMerge(source[key], target[key]);
		} else {
			result[key] = target[key];
		}
	}
	return result;
}

/**
 * Get breed benchmark for a specific breed
 * @param {Object} settings - CalfERP settings
 * @param {string} breed - Breed name (lowercase)
 * @returns {Object} Benchmark values or crossbred defaults
 */
export function getBreedBenchmark(settings, breed) {
	const benchmarks = settings?.breedBenchmarks || DEFAULT_CALF_ERP_SETTINGS.breedBenchmarks;
	const normalized = (breed || "").toLowerCase().trim();
	return benchmarks[normalized] || benchmarks.crossbred;
}

// ============================================================================
// PRODUCTION-TYPE AWARE ACCESS
// ============================================================================

/**
 * Resolve type-specific settings from an already-loaded config document
 * (as returned by getCalfERPSettings / CalfERPSettingsContext).
 * Synchronous companion to getERPSettingsForType for use in hooks.
 * @param {Object} mergedSettings - Full config-doc settings (calf at root)
 * @param {string} productionType - e.g. "calf" | "fattening"
 * @returns {Object} Settings for the requested type (defaults merged in)
 */
export function resolveERPSettingsForType(mergedSettings, productionType) {
	const config = getProductionTypeConfig(productionType);
	const defaults = DEFAULT_SETTINGS_BY_TYPE[config.key];

	if (config.settingsSection === "root") {
		return deepMerge(defaults, mergedSettings || {});
	}
	return deepMerge(defaults, mergedSettings?.[config.settingsSection] || {});
}

/**
 * Get ERP settings for a production type (with that type's defaults merged in).
 * Calf settings live at the document root (pre-dating sections); other types
 * live under their own section key of the same config document.
 * @param {string} userId
 * @param {string} productionType - e.g. "calf" | "fattening"
 * @returns {Promise<Object>} Merged settings for the requested type
 */
export async function getERPSettingsForType(userId, productionType) {
	const config = getProductionTypeConfig(productionType);
	const defaults = DEFAULT_SETTINGS_BY_TYPE[config.key];

	const doc = await db
		.collection("Users")
		.doc(userId)
		.collection(COLLECTION)
		.doc(DOC_ID)
		.get();

	if (!doc.exists) {
		return { ...defaults };
	}

	const data = doc.data();
	const sectionData =
		config.settingsSection === "root" ? data : data?.[config.settingsSection] || {};
	return deepMerge(defaults, sectionData);
}

/**
 * Save ERP settings for a production type (merge semantics).
 * @param {string} userId
 * @param {string} productionType - e.g. "calf" | "fattening"
 * @param {Object} settings - Partial settings for that type
 */
export async function saveERPSettingsForType(userId, productionType, settings) {
	const config = getProductionTypeConfig(productionType);

	if (config.settingsSection === "root") {
		await saveCalfERPSettings(userId, settings);
		return;
	}

	const cleanSettings = Object.fromEntries(
		Object.entries(settings).filter(([, v]) => v !== undefined)
	);

	await db
		.collection("Users")
		.doc(userId)
		.collection(COLLECTION)
		.doc(DOC_ID)
		.set(
			{ [config.settingsSection]: cleanSettings, updatedAt: Date.now() },
			{ merge: true }
		);
}
